import React, { useState, useEffect, useCallback } from 'react';
import { 
  Navigation, GraduationCap, School, 
  Hospital, TreePine, ShoppingBag, 
  Bus, Droplet, Lightbulb, Wifi,
  Utensils, PawPrint, Dumbbell,
  MapPin, Shield, Map, Check, ExternalLink
} from 'lucide-react';
import { Card } from '../ui/card';
import { GOOGLE_MAPS_API_KEY } from '../../config/keys';

// Componente personalizado para el ícono de Waze
const WazeIcon = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    {...props}
  >
    <path d="M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm2.126 8.474c.178-.11.351-.226.52-.35a.635.635 0 00.088-.957.635.635 0 00-.871-.094 8.339 8.339 0 00-.666.513c-.724.596-1.366 1.374-1.617 2.084a.635.635 0 00.387.813.636.636 0 00.814-.387c.165-.46.565-1.013 1.145-1.522zm-4.002-2.252a.635.635 0 00.891.033c.536-.464 1.178-.82 1.872-1.02a.635.635 0 00.428-.804.636.636 0 00-.804-.428c-.867.258-1.664.708-2.332 1.296a.635.635 0 00-.055.923zm1.146 4.748c-.727 0-1.32.593-1.32 1.32 0 .728.593 1.32 1.32 1.32.728 0 1.32-.592 1.32-1.32 0-.727-.592-1.32-1.32-1.32zm4.872 0c-.728 0-1.32.593-1.32 1.32 0 .728.592 1.32 1.32 1.32.727 0 1.32-.592 1.32-1.32 0-.727-.593-1.32-1.32-1.32z" />
  </svg>
);

// Utilitario para manipular arrays de valores
const parseArrayValues = (value) => {
  if (Array.isArray(value)) return value;
  
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return value.split(',').map(item => item.trim()).filter(Boolean);
    }
  }
  
  return [];
};

// Configuración de lugares cercanos
const NEARBY_PLACES_CONFIG = [
  { id: 'universidades', Icon: GraduationCap, label: 'Universidades', type: 'university', color: 'purple' },
  { id: 'colegios', Icon: School, label: 'Colegios', type: 'school', color: 'blue' },
  { id: 'hospitales', Icon: Hospital, label: 'Hospitales', type: 'hospital', color: 'red' },
  { id: 'parques', Icon: TreePine, label: 'Parques', type: 'park', color: 'green' },
  { id: 'centros_comerciales', Icon: ShoppingBag, label: 'Centros Comerciales', type: 'shopping_mall', color: 'orange' },
  { id: 'transporte_publico', Icon: Bus, label: 'Transporte Público', type: 'transit_station', color: 'yellow' }
];

// Configuración de servicios según el tipo de operación
const SERVICES_CONFIG = {
  comprar: [
    { id: 'agua', Icon: Droplet, label: 'Agua', color: 'blue' },
    { id: 'luz', Icon: Lightbulb, label: 'Luz', color: 'yellow' },
    { id: 'desague', Icon: Wifi, label: 'Desagüe', color: 'gray' },
    { id: 'internet', Icon: Wifi, label: 'Internet', color: 'green' }
  ],
  alquiler: [
    { id: 'cocina_equipada', Icon: Utensils, label: 'Cocina Equipada', color: 'orange' },
    { id: 'piscina', Icon: Wifi, label: 'Piscina', color: 'blue' },
    { id: 'pet_friendly', Icon: PawPrint, label: 'Pet Friendly', color: 'purple' },
    { id: 'gimnasio', Icon: Dumbbell, label: 'Gimnasio', color: 'red' }
  ]
};

const LocationSection = ({ property }) => {
  // Estados del componente
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [activePlace, setActivePlace] = useState(null);
  const [propertyMarker, setPropertyMarker] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [activeTab, setActiveTab] = useState('map'); // 'map' o 'places'

  // Obtener arrays de lugares y servicios
  const nearbyLocations = parseArrayValues(property.lugares_cercanos);
  const basicServices = parseArrayValues(property.servicios_basicos);

  // Cargar script de Google Maps
  useEffect(() => {
    // Verificar que exista latitud y longitud o al menos la dirección
    if (!property.latitude && !property.longitude && !property.mapa_direccion) {
      setMapError('No hay ubicación disponible');
      return;
    }

    // Verificar si Google Maps ya está cargado
    if (window.google?.maps) {
      setIsMapLoaded(true);
      return;
    }

    const loadGoogleMaps = () => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.defer = true;
      script.async = true;
      
      script.onload = () => {
        setIsMapLoaded(true);
      };

      script.onerror = () => {
        setMapError('Error al cargar Google Maps');
        console.error('Error loading Google Maps script');
      };

      document.head.appendChild(script);
    };

    // Cargar script solo si aún no ha sido cargado
    if (!document.querySelector(`script[src*="maps.googleapis.com"]`)) {
      loadGoogleMaps();
    }

    return () => {
      markers.forEach(marker => marker?.setMap(null));
      if (propertyMarker) propertyMarker.setMap(null);
    };
  }, [property.mapa_direccion, property.latitude, property.longitude]);

  // Inicializar mapa
  useEffect(() => {
    if (!isMapLoaded || (!property.latitude && !property.longitude && !property.mapa_direccion) || !window.google?.maps) {
      return;
    }

    const initializeMap = () => {
      try {
        const mapElement = document.getElementById('propertyMap');
        if (!mapElement) return;

        const mapInstance = new window.google.maps.Map(mapElement, {
          zoom: 15,
          mapTypeControl: false,
          streetViewControl: true,
          fullscreenControl: true,
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            }
          ]
        });

        // Si tenemos coordenadas, las usamos directamente
        if (property.latitude && property.longitude) {
          const location = new window.google.maps.LatLng(
            parseFloat(property.latitude),
            parseFloat(property.longitude)
          );
          
          mapInstance.setCenter(location);
          
          const marker = new window.google.maps.Marker({
            map: mapInstance,
            position: location,
            title: property.titulo,
            icon: {
              url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
              scaledSize: new window.google.maps.Size(40, 40)
            },
            animation: window.google.maps.Animation.DROP,
            zIndex: 1000
          });

          setPropertyMarker(marker);
        } 
        // Si no tenemos coordenadas, geocodificamos la dirección
        else if (property.mapa_direccion) {
          const geocoder = new window.google.maps.Geocoder();
          
          geocoder.geocode({ address: property.mapa_direccion }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
              const location = results[0].geometry.location;
              mapInstance.setCenter(location);
              
              const marker = new window.google.maps.Marker({
                map: mapInstance,
                position: location,
                title: property.titulo,
                icon: {
                  url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                  scaledSize: new window.google.maps.Size(40, 40)
                },
                animation: window.google.maps.Animation.DROP,
                zIndex: 1000
              });

              setPropertyMarker(marker);
            } else {
              console.error('Geocode error:', status);
              setMapError(`Error al localizar la dirección: ${property.mapa_direccion}`);
            }
          });
        }

        setMap(mapInstance);
      } catch (error) {
        console.error('Error initializing map:', error);
        setMapError('Error al inicializar el mapa');
      }
    };

    // Pequeño retraso para asegurar que Google Maps esté completamente cargado
    const timeoutId = setTimeout(initializeMap, 100);
    return () => clearTimeout(timeoutId);
  }, [isMapLoaded, property.mapa_direccion, property.titulo, property.latitude, property.longitude]);

  // Limpiar marcadores existentes
  const clearMarkers = useCallback(() => {
    markers.forEach(marker => marker?.setMap(null));
    setMarkers([]);
  }, [markers]);

  // Buscar lugares cercanos
  const searchNearbyPlaces = useCallback((placeType) => {
    if (!map || !isMapLoaded || !window.google?.maps) return;

    clearMarkers();
    const service = new window.google.maps.places.PlacesService(map);
    
    service.nearbySearch({
      location: map.getCenter(),
      radius: 1000,
      type: placeType
    }, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        const newMarkers = results.map(place => {
          const marker = new window.google.maps.Marker({
            map: map,
            position: place.geometry.location,
            title: place.name,
            icon: {
              url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
              scaledSize: new window.google.maps.Size(30, 30)
            },
            animation: window.google.maps.Animation.DROP
          });

          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div class="p-3 min-w-[200px]">
                <h3 class="font-medium text-blue-500">${place.name}</h3>
                <p class="text-sm text-gray-600 mt-1">
                  ${place.vicinity || 'Sin dirección disponible'}
                </p>
                ${place.rating ? `
                  <div class="flex items-center gap-1 mt-2">
                    <span class="text-yellow-400">★</span>
                    <span class="text-sm">${place.rating}</span>
                  </div>
                ` : ''}
              </div>
            `
          });

          marker.addListener('click', () => {
            infoWindow.open(map, marker);
          });

          return marker;
        });
        setMarkers(newMarkers);
      }
    });
  }, [map, clearMarkers, isMapLoaded]);

  // Manejar clic en lugar cercano
  const handlePlaceClick = useCallback((placeId, placeType) => {
    if (!nearbyLocations.includes(placeId) || !isMapLoaded) return;
    
    // Cambiar a la pestaña de mapa al hacer clic en un lugar cercano
    setActiveTab('map');

    setActivePlace(prevActive => prevActive === placeId ? null : placeId);

    if (activePlace !== placeId) {
      searchNearbyPlaces(placeType);
    } else {
      clearMarkers();
    }
  }, [activePlace, clearMarkers, isMapLoaded, nearbyLocations, searchNearbyPlaces]);

  // Configuración de servicios según tipo de operación
  const servicesConfig = SERVICES_CONFIG[property.operacion] || SERVICES_CONFIG.comprar;

  // Generar URL para Google Maps y Waze
  const getGoogleMapsUrl = () => {
    if (property.latitude && property.longitude) {
      return `https://www.google.com/maps/search/?api=1&query=${property.latitude},${property.longitude}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.mapa_direccion)}`;
  };
  
  const getWazeUrl = () => {
    if (property.latitude && property.longitude) {
      return `https://www.waze.com/ul?ll=${property.latitude},${property.longitude}&navigate=yes`;
    }
    return `https://www.waze.com/ul?q=${encodeURIComponent(property.mapa_direccion)}&navigate=yes`;
  };

  // Configuración de colores para componentes
  const getColorConfig = (color) => {
    const configs = {
      purple: { 
        bg: 'backdrop-blur-xl bg-purple-50/80 dark:bg-purple-500/20', 
        text: 'text-purple-600 dark:text-purple-400', 
        border: 'border-purple-200 dark:border-purple-500/30', 
        active: 'bg-purple-100 dark:bg-purple-500/30' 
      },
      blue: { 
        bg: 'backdrop-blur-xl bg-blue-50/80 dark:bg-blue-500/20', 
        text: 'text-blue-600 dark:text-blue-400', 
        border: 'border-blue-200 dark:border-blue-500/30', 
        active: 'bg-blue-100 dark:bg-blue-500/30' 
      },
      red: { 
        bg: 'backdrop-blur-xl bg-red-50/80 dark:bg-red-500/20', 
        text: 'text-red-600 dark:text-red-400', 
        border: 'border-red-200 dark:border-red-500/30', 
        active: 'bg-red-100 dark:bg-red-500/30' 
      },
      green: { 
        bg: 'backdrop-blur-xl bg-emerald-50/80 dark:bg-emerald-500/20', 
        text: 'text-emerald-600 dark:text-emerald-400', 
        border: 'border-emerald-200 dark:border-emerald-500/30', 
        active: 'bg-emerald-100 dark:bg-emerald-500/30' 
      },
      orange: { 
        bg: 'backdrop-blur-xl bg-orange-50/80 dark:bg-orange-500/20', 
        text: 'text-orange-600 dark:text-orange-400', 
        border: 'border-orange-200 dark:border-orange-500/30', 
        active: 'bg-orange-100 dark:bg-orange-500/30' 
      },
      yellow: { 
        bg: 'backdrop-blur-xl bg-yellow-50/80 dark:bg-yellow-500/20', 
        text: 'text-yellow-600 dark:text-yellow-400', 
        border: 'border-yellow-200 dark:border-yellow-500/30', 
        active: 'bg-yellow-100 dark:bg-yellow-500/30' 
      },
      gray: { 
        bg: 'backdrop-blur-xl bg-gray-50/80 dark:bg-white/5', 
        text: 'text-gray-600 dark:text-gray-400', 
        border: 'border-gray-200 dark:border-white/20', 
        active: 'bg-gray-100 dark:bg-white/10' 
      }
    };
    return configs[color] || configs.blue;
  };

  // Componentes de UI
  const LocationTab = ({ id, label, icon: Icon, active, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all duration-300 
                border-2 ${active 
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-400 dark:to-indigo-500 text-white border-blue-500 dark:border-blue-400 shadow-lg scale-105' 
                  : 'backdrop-blur-xl bg-white/80 dark:bg-white/10 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-white/20 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/20 hover:scale-102'}`}
    >
      <div className={`p-2 rounded-lg ${active ? 'bg-white/20' : 'bg-gray-100 dark:bg-white/10'}`}>
        <Icon className={`w-5 h-5 ${active ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
      </div>
      <span>{label}</span>
    </button>
  );

  const MapLegendItem = ({ color, label }) => (
    <div className="flex items-center gap-3 p-2 backdrop-blur-xl bg-white/80 dark:bg-white/10 rounded-lg border border-white/50 dark:border-white/20">
      <div className={`w-4 h-4 rounded-full ${color} border-2 border-white dark:border-gray-300 shadow-sm`} />
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
    </div>
  );

  const PlaceButton = ({ icon: Icon, label, isAvailable, isActive, onClick, color = 'blue' }) => {
    const colors = getColorConfig(color);
    
    return (
      <button
        onClick={onClick}
        disabled={!isAvailable}
        className={`flex items-center gap-4 w-full p-4 rounded-xl transition-all duration-300
                  border-2 ${isActive ? `${colors.border} ${colors.bg} shadow-lg dark:shadow-black/30 scale-105` : 'border-gray-200 dark:border-white/20'}
                  ${isAvailable 
                    ? 'hover:shadow-md dark:hover:shadow-black/30 hover:-translate-y-1 cursor-pointer hover:border-gray-300 dark:hover:border-white/30' 
                    : 'opacity-50 cursor-not-allowed'
                  } 
                  ${isActive 
                    ? colors.bg
                    : 'backdrop-blur-xl bg-white/80 dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10'
                  }`}
      >
        <div className={`p-3 rounded-xl ${isActive ? colors.active : 'bg-gray-100 dark:bg-white/10'}`}>
          <Icon className={`w-6 h-6 ${isActive ? colors.text : 'text-gray-500 dark:text-gray-400'}`} />
        </div>
        <div className="flex-1 text-left">
          <span className={`font-semibold ${isActive ? colors.text : 'text-gray-700 dark:text-gray-300'}`}>{label}</span>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {isAvailable ? 'Disponible en la zona' : 'No disponible'}
          </p>
        </div>
        
        {isActive && (
          <div className={`p-1 rounded-full ${colors.active}`}>
            <Check className={`w-5 h-5 ${colors.text}`} />
          </div>
        )}
      </button>
    );
  };

  const ServiceItem = ({ icon: Icon, label, isAvailable, color = 'blue' }) => {
    const colors = getColorConfig(color);
    
    return (
      <div className={`flex items-center gap-3 p-4 rounded-xl border transition-all duration-300
                    ${isAvailable 
                      ? `${colors.bg} ${colors.border} shadow-sm dark:shadow-black/20` 
                      : 'backdrop-blur-xl bg-gray-50/80 dark:bg-white/5 border-gray-200 dark:border-white/20'}`}>
        <div className={`p-2 rounded-lg ${isAvailable ? colors.active : 'bg-gray-200 dark:bg-white/10'}`}>
          <Icon className={`w-5 h-5 ${isAvailable ? colors.text : 'text-gray-500 dark:text-gray-400'}`} />
        </div>
        <div className="flex-1">
          <span className={`font-semibold text-sm ${isAvailable ? colors.text : 'text-gray-500 dark:text-gray-400'}`}>
            {label}
          </span>
        </div>
        
        {isAvailable && (
          <div className={`p-1 rounded-full ${colors.active}`}>
            <Check className={`w-4 h-4 ${colors.text}`} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Header mejorado */}
      <div className="text-center mb-10">
        <div className="relative mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-0.5 w-12 bg-gradient-to-r from-transparent to-teal-500 dark:to-teal-400 rounded-full"></div>
            <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-600 dark:from-teal-400 dark:to-cyan-500 rounded-2xl shadow-lg dark:shadow-black/30">
              <Navigation className="w-6 h-6 text-white" />
            </div>
            <div className="h-0.5 w-12 bg-gradient-to-l from-transparent to-cyan-500 dark:to-cyan-400 rounded-full"></div>
          </div>
          
          <h2 className="text-3xl font-bold bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 dark:from-teal-400 dark:via-cyan-400 dark:to-blue-400
                         bg-clip-text text-transparent mb-3">
            Ubicación y Servicios
          </h2>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-teal-400 dark:bg-teal-300 rounded-full animate-pulse"></div>
            <div className="w-16 h-0.5 bg-gradient-to-r from-teal-400 to-cyan-400 dark:from-teal-300 dark:to-cyan-300 rounded-full"></div>
            <div className="w-2 h-2 bg-cyan-400 dark:bg-cyan-300 rounded-full animate-pulse delay-75"></div>
          </div>
        </div>
        
        <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          <MapPin className="w-5 h-5 flex-shrink-0 text-teal-500 dark:text-teal-400" />
          <p className="text-lg">{property.mapa_direccion}</p>
        </div>
      </div>

      <Card className="backdrop-blur-xl bg-white/80 dark:bg-white/5 shadow-xl dark:shadow-black/50 rounded-2xl overflow-hidden border border-gray-100 dark:border-white/10">        
        {/* Tabs */}
        <div className="p-6 bg-gradient-to-r from-gray-50/80 to-blue-50/80 dark:from-gray-800/50 dark:to-blue-900/50 border-b border-gray-200 dark:border-white/10">
          <div className="flex gap-4 justify-center">
            <LocationTab 
              id="map" 
              label="Ver en Mapa" 
              icon={Map} 
              active={activeTab === 'map'} 
              onClick={() => setActiveTab('map')} 
            />
            <LocationTab 
              id="places" 
              label="Lugares Cercanos" 
              icon={Navigation} 
              active={activeTab === 'places'} 
              onClick={() => setActiveTab('places')} 
            />
          </div>
        </div>
        
        {/* Content Area */}
        <div className="p-6">
          {activeTab === 'map' ? (
            <div className="space-y-6">
              {/* Mapa */}
              <div className="relative bg-gray-100 dark:bg-gray-800 rounded-2xl overflow-hidden h-[500px] shadow-inner dark:shadow-black/50">
                <div id="propertyMap" className="w-full h-full">
                  {mapError && (
                    <div className="absolute inset-0 flex items-center justify-center backdrop-blur-xl bg-gray-100/90 dark:bg-gray-800/90">
                      <div className="flex items-center gap-3 px-6 py-4 rounded-xl backdrop-blur-xl bg-red-50/80 dark:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30">
                        <MapPin className="w-5 h-5" />
                        <span className="font-medium">{mapError}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Map Legend */}
                <div className="absolute bottom-6 left-6 space-y-2">
                  <MapLegendItem color="bg-red-500" label="Ubicación de la propiedad" />
                  {markers.length > 0 && (
                    <MapLegendItem 
                      color="bg-blue-500" 
                      label={NEARBY_PLACES_CONFIG.find(p => p.id === activePlace)?.label || 'Lugares cercanos'} 
                    />
                  )}
                </div>
              </div>
              
              {/* Navigation Links */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <a
                  href={getGoogleMapsUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 py-4 px-6 
                          bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-400 dark:to-blue-500 text-white rounded-xl shadow-lg dark:shadow-black/30
                          hover:shadow-xl dark:hover:shadow-black/50 hover:scale-105 transition-all duration-300"
                >
                  <Map className="w-6 h-6" />
                  <span className="font-semibold">Abrir en Google Maps</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a
                  href={getWazeUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 py-4 px-6 
                          backdrop-blur-xl bg-white/80 dark:bg-white/10 text-gray-700 dark:text-gray-200 rounded-xl shadow-lg dark:shadow-black/30 border-2 border-gray-200 dark:border-white/20
                          hover:shadow-xl dark:hover:shadow-black/50 hover:scale-105 transition-all duration-300"
                >
                  <WazeIcon className="w-6 h-6 text-blue-400" />
                  <span className="font-semibold">Navegar con Waze</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              {/* Servicios en vista de mapa */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="backdrop-blur-xl bg-gradient-to-br from-emerald-50/80 to-green-50/80 dark:from-emerald-500/20 dark:to-green-500/20 rounded-2xl p-6 border border-emerald-200 dark:border-emerald-500/30">
                  <h4 className="text-xl font-bold text-emerald-800 dark:text-emerald-300 mb-4 flex items-center gap-2">
                    <Shield className="w-6 h-6" />
                    Servicios Básicos
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {servicesConfig.map(({ id, Icon, label, color }) => (
                      <ServiceItem
                        key={id}
                        icon={Icon}
                        label={label}
                        isAvailable={basicServices.includes(id)}
                        color={color}
                      />
                    ))}
                  </div>
                </div>
                
                <div className="backdrop-blur-xl bg-gradient-to-br from-blue-50/80 to-indigo-50/80 dark:from-blue-500/20 dark:to-indigo-500/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-500/30">
                  <h4 className="text-xl font-bold text-blue-800 dark:text-blue-300 mb-4 flex items-center gap-2">
                    <TreePine className="w-6 h-6" />
                    Servicios Adicionales
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <ServiceItem 
                      icon={TreePine} 
                      label="Áreas Verdes" 
                      isAvailable={property.areas_verdes === 'Si'} 
                      color="green"
                    />
                    <ServiceItem 
                      icon={Shield} 
                      label="Seguridad" 
                      isAvailable={basicServices.includes('seguridad')} 
                      color="gray"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">
                  Explorar Lugares Cercanos
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Haz clic en cualquier lugar disponible para verlo en el mapa
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {NEARBY_PLACES_CONFIG.map(({ id, Icon, label, type, color }) => (
                  <PlaceButton
                    key={id}
                    icon={Icon}
                    label={label}
                    isAvailable={nearbyLocations.includes(id)}
                    isActive={activePlace === id}
                    onClick={() => handlePlaceClick(id, type)}
                    color={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default LocationSection;